import React, { useState } from "react";
import IntegrationDataMapping from "../shared/IntegrationDataMapping";

// ---------------------------------------------------------------------------
// Guide sub-components
// ---------------------------------------------------------------------------

type GuideSection = "overview" | "agent-panel" | "workflow-data" | "ai-tools";

const GUIDE_SECTIONS: { id: GuideSection; title: string }[] = [
  { id: "overview",       title: "What is field mapping?" },
  { id: "agent-panel",    title: "Agent CRM panel" },
  { id: "workflow-data",  title: "HTTP POST / workflows" },
  { id: "ai-tools",       title: "AI tools (no mapping)" },
];

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-primary-bg border border-cornflower-blue-200 rounded-lg p-3 mt-3 text-xs text-cornflower-blue-800 leading-relaxed">
      {children}
    </div>
  );
}

function TermBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3 text-xs text-slate-600 leading-relaxed">
      {children}
    </div>
  );
}

function InlineCode({ children }: { children: string }) {
  return (
    <code className="bg-white px-1 py-0.5 rounded border border-slate-200 text-cornflower-blue-800 font-mono text-[11px]">
      {children}
    </code>
  );
}

function GuideOverview() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">What is Field Mapping?</h3>
      <p className="mb-3">
        Field mapping tells the platform <strong>which QuickBooks data to surface in the agent CRM sidebar</strong> and what to call it. When an agent opens a conversation, the sidebar shows customer and financial information pulled from QuickBooks — field mapping controls exactly which fields appear and their display labels.
      </p>
      <p className="mb-3">
        Think of it as a <em>view configuration</em>: you pick a QuickBooks object type (Customer, Invoice, Payment, Estimate), choose the fields you care about, give them friendly labels, and decide whether agents can search on them.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-2">Quick decision guide</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Use case</th>
              <th className="px-3 py-2 text-left">Field mapping needed?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-3 py-2">Show customer data in the agent sidebar</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — required</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Pass QuickBooks data into an HTTP POST workflow node</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — required</td>
            </tr>
            <tr>
              <td className="px-3 py-2">AI responds to visitor questions about invoices/payments</td>
              <td className="px-3 py-2 text-slate-500 font-medium">No — AI reads raw data</td>
            </tr>
          </tbody>
        </table>
      </div>

      <TermBox>
        <strong>Scope type</strong> — QuickBooks data is grouped into object types: Customer, Invoice, Payment, Estimate. Each mapping panel targets one scope type. You can add multiple panels to map fields from different object types.
      </TermBox>
    </div>
  );
}

function GuideAgentPanel() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">Agent CRM Panel</h3>
      <p className="mb-3">
        When an agent handles a conversation, the right-side CRM panel displays a contact's linked QuickBooks data. The fields shown, their order, and their labels all come from this mapping configuration.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Field options explained</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Option</th>
              <th className="px-3 py-2 text-left">Effect</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-3 py-2 font-medium">Label</td>
              <td className="px-3 py-2">The display name agents see. Rename <InlineCode>total_amt</InlineCode> to "Invoice Total".</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2 font-medium">Show in search</td>
              <td className="px-3 py-2">Agents can search conversations by this field value (e.g. search by invoice number).</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-medium">Show when selected</td>
              <td className="px-3 py-2">Field appears in the contact detail view when the agent drills into a record.</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2 font-medium">Default value</td>
              <td className="px-3 py-2">Fallback text when the QuickBooks field is empty or null.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox>
        <strong>Tip:</strong> Map only the fields your agents actually need to take action. A focused card with balance, due date, and status is more useful than all 20 invoice fields.
      </InfoBox>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">QuickBooks object reference</h4>
      <CodeBlock>{`Customer: id, display_name, email, phone, balance, currency_ref, billing_addr
Invoice:  id, doc_number, customer_name, total_amt, balance, due_date, status
Payment:  id, total_amt, customer_name, deposit, payment_method_ref, txn_date
Estimate: id, doc_number, customer_name, total_amt, expiry_date, status`}</CodeBlock>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Customer mapping</h4>
      <CodeBlock>{`QuickBooks Field   Label              Search  Show
──────────────────────────────────────────────────
email              Email              ✓       ✓
display_name       Customer Name      ✓       ✓
phone              Phone                      ✓
balance            Open Balance               ✓
currency_ref       Currency                   ✓`}</CodeBlock>
    </div>
  );
}

function GuideWorkflowData() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">HTTP POST / Workflow Data Passing</h3>
      <p className="mb-3">
        In a workflow, an <strong>HTTP POST node</strong> can call an external API and include QuickBooks data in the request body. To pass a QuickBooks field value, you first map it — that gives it a variable name — then reference that variable in the POST node.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">How the variable is formed</h4>
      <p className="text-xs text-slate-600 mb-2">
        Once you map a QuickBooks field, it becomes available as a workflow variable. The variable name follows the pattern <InlineCode>{"{quickbooks.scopeType.fieldName}"}</InlineCode>:
      </p>
      <CodeBlock>{`Scope: Invoice   →  Field: balance
Variable: {quickbooks.invoice.balance}

Scope: Customer  →  Field: email
Variable: {quickbooks.customer.email}`}</CodeBlock>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Send to a support ticketing system</h4>
      <CodeBlock>{`// HTTP POST node — request body
{
  "contact_email":   "{quickbooks.customer.email}",
  "invoice_number":  "{quickbooks.invoice.doc_number}",
  "balance_due":     "{quickbooks.invoice.balance}",
  "due_date":        "{quickbooks.invoice.due_date}",
  "conversation":    "{conversation.id}"
}`}</CodeBlock>

      <InfoBox>
        <strong>Field mapping is the bridge.</strong> Without a mapping entry for <InlineCode>invoice.balance</InlineCode>, the variable <InlineCode>{"{quickbooks.invoice.balance}"}</InlineCode> would be empty at runtime. Map first, then reference in the POST node.
      </InfoBox>
    </div>
  );
}

function GuideAiTools() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">AI Tools — No Field Mapping Required</h3>
      <p className="mb-3">
        QuickBooks AI tools work without field mapping — they look up invoices, check balances, retrieve payment history, and create customers. Field mapping configures what appears in the agent sidebar.
      </p>

      <h4 className="font-medium text-slate-700 mt-3 mb-1">How AI tools work</h4>
      <p className="text-xs text-slate-600 mb-2">
        When an AI workflow node runs, it calls the QuickBooks API at runtime and receives the full raw JSON response. The AI model extracts relevant details and composes a natural language response — no schema configuration needed.
      </p>
      <CodeBlock>{`// What the AI receives internally
{
  "customer": {
    "id": "99",
    "display_name": "Jane Smith",
    "email": "jane@acme.com",
    "balance": 1450.00
  },
  "invoices": [
    { "doc_number": "INV-1042", "total_amt": 1450.00, "due_date": "2026-04-01", "status": "Open" }
  ]
}

// What the AI says to the visitor
"Hi Jane! You have 1 open invoice — INV-1042 for $1,450.00
 due April 1st. Would you like me to send you a payment link?"`}</CodeBlock>

      <InfoBox>
        <strong>Why no mapping?</strong> Field mapping is a <em>static display config</em> — you define once what to show in the sidebar. AI tools are <em>dynamic runtime calls</em> — the AI decides which fields matter based on the visitor's question.
      </InfoBox>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Comparison: AI tool vs mapped field</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs mt-2">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Capability</th>
              <th className="px-3 py-2 text-left">AI Tool</th>
              <th className="px-3 py-2 text-left">Field Mapping</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs">
            <tr>
              <td className="px-3 py-2">Agent sidebar display</td>
              <td className="px-3 py-2 text-slate-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Visitor-facing AI answers</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
              <td className="px-3 py-2 text-slate-400">No</td>
            </tr>
            <tr>
              <td className="px-3 py-2">HTTP POST variable injection</td>
              <td className="px-3 py-2 text-slate-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Requires configuration</td>
              <td className="px-3 py-2 text-slate-500">No (auto-activated)</td>
              <td className="px-3 py-2 text-slate-500">Yes (this panel)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <TermBox>
        <strong>When you need both:</strong> An AI workflow node can answer "what is my balance?" without any mapping. But if the same workflow also sends a POST to your CRM, you need field mapping to supply the <InlineCode>{"{quickbooks.customer.balance}"}</InlineCode> variable that goes into the POST body.
      </TermBox>
    </div>
  );
}

function FieldMappingGuide() {
  const [guideSection, setGuideSection] = useState<GuideSection>("overview");
  return (
    <div className="bg-white rounded-lg border border-slate-200 mb-4">
      <div className="flex">
        {/* Sidebar nav */}
        <nav className="w-48 shrink-0 border-r border-slate-200 p-3" aria-label="Field mapping guide sections">
          <div className="space-y-0.5">
            {GUIDE_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setGuideSection(s.id)}
                className={`block w-full text-left px-3 py-1.5 rounded text-xs transition-colors ${
                  guideSection === s.id
                    ? "bg-primary-bg text-primary-active font-medium"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 p-5 text-sm text-slate-700 leading-relaxed overflow-y-auto max-h-[520px]">
          {guideSection === "overview"      && <GuideOverview />}
          {guideSection === "agent-panel"   && <GuideAgentPanel />}
          {guideSection === "workflow-data" && <GuideWorkflowData />}
          {guideSection === "ai-tools"      && <GuideAiTools />}
        </div>
      </div>
    </div>
  );
}

const SCOPE_OPTIONS = ["Customer", "Invoice", "Payment", "Estimate"];

interface Props {
  teamId: number;
  selectedConfigId: number;
}

export default function QuickBooksDataMapping(props: Props) {
  return (
    <IntegrationDataMapping
      selectedConfigId={props.selectedConfigId}
      teamId={props.teamId}
      description="Configure which QuickBooks fields appear in the agent CRM sidebar and as workflow variables."
      guide={<FieldMappingGuide />}
      guideLabel="Field Mapping Guide ▾"
      fieldKey="QuickBooksField"
      fieldLabel="QuickBooks Field"
      apiPrefix="QuickBooks/Integrations"
      getFieldsPath="GetQuickBooksFields"
      scopeOptions={SCOPE_OPTIONS}
    />
  );
}
